import type { Dispatch, SetStateAction } from 'react'

import type { TeamMemberDto } from '@api'
import type { Row } from '@tanstack/react-table'

import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { useToast } from '@/components/ui/toast.tsx'
import { useUpdateTeamTable } from '@/modules/team/hooks/use-update-table'
import { handleError } from '@/utils/error'

interface TeamRemoveDialogType {
  row: Row<TeamMemberDto>
  showRemoveDialog: boolean
  setRemoveEditDialog: Dispatch<SetStateAction<boolean>>
}

export const TeamRemoveDialog = ({
  row,
  showRemoveDialog,
  setRemoveEditDialog,
}: TeamRemoveDialogType) => {
  const { deleteMember } = useUpdateTeamTable()

  const toast = useToast()

  const handleCloseDialog = () => {
    setRemoveEditDialog(false)
  }

  const handleSubmit = async () => {
    await deleteMember
      .mutateAsync(row.original.id)
      .then(() => {
        toast.success('Successfully removed user')
      })
      .catch((error) => {
        handleError(
          error,
          'Something went wrong while removing the user. Try again later.',
        )
      })

    handleCloseDialog()
  }

  return (
    <Dialog
      show={showRemoveDialog}
      onClose={handleCloseDialog}
    >
      <Dialog.Header>
        <Dialog.Title>Are you sure?</Dialog.Title>
        <Dialog.Description className="text-center">
          This action cannot be undone and will permanently remove the following
          member: {row.original.name}
        </Dialog.Description>
      </Dialog.Header>

      <Dialog.ClosableFooter
        onClose={handleCloseDialog}
        className="mt-6"
      >
        <Button
          variant="destructive"
          onClick={handleSubmit}
          isLoading={deleteMember.isLoading}
        >
          Remove
        </Button>
      </Dialog.ClosableFooter>
    </Dialog>
  )
}
